import { useEffect, useState } from "react";

export const OAUTH_BANNER_STATUS_CONFIRM_MS = 12_000;

export interface ConfirmedQueryFailureInput {
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
}

/** Failed query that is not waiting on the initial load or an in-flight refetch. */
export function isSettledQueryFailure(query: ConfirmedQueryFailureInput): boolean {
  return query.isError && !query.isFetching && !query.isPending;
}

/**
 * True only after a query has stayed failed (settled, not refetching) for confirmDelayMs.
 * Clears immediately on success, pending, or an in-flight refetch so transient blips do not alarm.
 */
export function useConfirmedQueryFailure(
  query: ConfirmedQueryFailureInput,
  confirmDelayMs: number = OAUTH_BANNER_STATUS_CONFIRM_MS,
): boolean {
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!isSettledQueryFailure(query)) {
      setConfirmed(false);
      return;
    }

    const timer = window.setTimeout(() => setConfirmed(true), confirmDelayMs);
    return () => window.clearTimeout(timer);
  }, [query.isError, query.isFetching, query.isPending, confirmDelayMs]);

  return confirmed;
}
